/**
 * src/lib/offline/syncEngine.js — drains the offline outbox when back online.
 *
 * Singleton. Subscribes to connectivity; on `online` it replays queued mutations
 * FIFO against the real task API, removing each op on success and backing off
 * (keep + retry) on network failure. Last-write-wins by virtue of replay order.
 *
 * Idempotent replay:
 *   - create carries a client uuid → a PK conflict means it already synced (treat
 *     as done, drop the op).
 *   - update/status/assignee/archive key on entity_id → re-applying is safe.
 * FIFO guarantees a create lands before its later edits (same entity_id).
 */

import { listPendingIds, getOp, remove, markFailed, count } from './outbox';
import { getOnline, subscribe as subscribeNet } from './networkStatus';
import {
  createTask,
  updateTask,
  updateTaskStatus,
  updateTaskAssignee,
  archiveTask,
} from '../api/tasks';
import { createProject, updateProject, moveProjectStage, updateBundle } from '../api/projects';
import { createSupplier, updateSupplier, createPurchaseOrder } from '../api/procurement';
import {
  updateCRMStatus, updateCRMDeal, updateVessel, updateVesselContact, deleteVesselContact,
  createVessel, createVesselContact, createCRMDeal, createCRMActivityFromPayload, updateCRMActivity,
} from '../api/crm';
import { createQuote, updateQuote, saveQuoteLines, saveQuoteSections } from '../api/quotes';
import { uploadPhotoBufferAndRegister } from './photoApplier';
import { logAppError } from '../errorReporter';

const MAX_RETRIES = 8;
const DUP_RE = /duplicate key|already exists|23505/i;
const NET_RE = /failed to fetch|networkerror|network request failed|load failed|offline|timeout/i;

function isNetworkErr(msg) {
  return NET_RE.test(String(msg || ''));
}

/**
 * Pure predicate: should the app reconcile optimistic state with the server now?
 * True only when a drain just FINISHED (syncing=false) AND the outbox is fully
 * flushed (pendingCount=0) AND something actually synced. Gating on an empty
 * outbox means still-queued (failed/pending) edits keep their optimistic UI
 * instead of being reverted by a premature reload.
 */
export function shouldReconcileAfterSync(evt) {
  return !!evt
    && evt.syncing === false
    && evt.pendingCount === 0
    && !!evt.lastResult
    && (evt.lastResult.synced || 0) > 0;
}

/** Default replay map. tasks domain → op type → api call. */
const DEFAULT_APPLIERS = {
  tasks: {
    create: (op) => createTask({ ...op.payload, id: op.entity_id }),
    update: (op) => updateTask({ record_id: op.entity_id, ...op.payload }),
    status: (op) => updateTaskStatus(op.entity_id, op.payload.status),
    assignee: (op) => updateTaskAssignee(op.entity_id, op.payload.assignee_id),
    archive: (op) => archiveTask(op.entity_id),
  },
  projects: {
    create: (op) => createProject({ ...op.payload, id: op.entity_id }),
    update: (op) => updateProject(op.entity_id, op.payload),
    move_stage: (op) => moveProjectStage(op.entity_id, op.payload.stage),
    update_bundle: (op) => updateBundle(op.entity_id, op.payload),
  },
  procurement: {
    // Offline CREATEs replay with the client-supplied id (PK-idempotent; a
    // conflict means it already synced → create-dup detection drops it).
    create_supplier: (op) => createSupplier({ ...op.payload, id: op.entity_id }),
    update_supplier: (op) => updateSupplier(op.entity_id, op.payload),
    create_po: (op) => createPurchaseOrder({ ...op.payload, id: op.entity_id }),
  },
  crm: {
    update_status: (op) => updateCRMStatus(op.entity_id, op.payload.status),
    update_deal: (op) => updateCRMDeal(op.entity_id, op.payload),
    update_vessel: (op) => updateVessel(op.entity_id, op.payload),
    update_contact: (op) => updateVesselContact(op.entity_id, op.payload),
    delete_contact: (op) => deleteVesselContact(op.entity_id),
    // Offline CREATEs replay with the client-supplied id (replay-idempotent; a
    // PK conflict means it already synced → create-dup detection drops it).
    create_vessel: (op) => createVessel({ ...op.payload, id: op.entity_id }),
    create_contact: (op) => createVesselContact({ ...op.payload, id: op.entity_id }),
    create_deal: (op) => createCRMDeal({ ...op.payload, id: op.entity_id }),
    // Notes/timeline: crm_activities create + edit.
    create_activity: (op) => createCRMActivityFromPayload({ ...op.payload, id: op.entity_id }),
    update_activity: (op) => updateCRMActivity(op.entity_id, op.payload),
  },
  // TICKET-262 offline quotes: build a quote offline, replay on reconnect.
  // create replays with the client-supplied id (PK-idempotent — handleQuotesCreate
  // returns the existing row on a 23505 conflict). save_lines is a bulk atomic
  // replace (one op covers N lines); save_sections upserts by id. PDF render is
  // deferred to online (Gotenberg needs the persisted quote).
  quotes: {
    create: (op) => createQuote({ ...op.payload, id: op.entity_id }),
    update: (op) => updateQuote({ id: op.entity_id, ...op.payload }),
    // TICKET-941: replay the ids the builder had seen; an op queued before this
    // field existed has none, so the server keeps its legacy replace-all behaviour.
    save_lines: (op) => {
      const { lines, ...guards } = op.payload; // known_line_ids / configure_seen_at
      return Object.keys(guards).length ? saveQuoteLines(op.entity_id, lines, guards) : saveQuoteLines(op.entity_id, lines);
    },
    save_sections: (op) => saveQuoteSections(op.entity_id, op.payload.sections),
  },
  // Photos Phase 1: a queued capture's create op carries the image Blob + scope +
  // product_types + content_sha256 in op.payload. Drain = upload the Blob to the
  // photo-buffer Supabase bucket, then photos.register. Idempotent on the client
  // content_sha256 (server dedups → deduped:true), so a replay is a no-op.
  photos: {
    create: (op) => uploadPhotoBufferAndRegister({ ...op.payload, client_id: op.entity_id }),
  },
};

let appliers = DEFAULT_APPLIERS;
let netUnsub = null;
let running = false;
let syncing = false;
const subscribers = new Set();
// TICKET-815: a drain that leaves ops behind used to park them until the next
// reconnect or capture — with nothing else scheduled, one timed-out photo left
// 55 already-registered tiles stuck on "Uploading..." forever. Re-drain on a
// bounded backoff instead. Reset whenever a drain makes progress.
let retryTimer = null;
let retryAttempt = 0;
const RETRY_BACKOFF_MS = [3000, 10000, 30000];
let retryBackoff = RETRY_BACKOFF_MS;

/** Override the replay map (tests). */
export function setAppliers(map) { appliers = map; }
/** Shorten the retry backoff (tests only). */
export function __setRetryBackoff(arr) { retryBackoff = Array.isArray(arr) && arr.length ? arr : RETRY_BACKOFF_MS; }
/** Reset to defaults (tests). */
export function __reset() { appliers = DEFAULT_APPLIERS; retryBackoff = RETRY_BACKOFF_MS; running = false; syncing = false; if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; } retryAttempt = 0; if (netUnsub) { netUnsub(); netUnsub = null; } subscribers.clear(); }

/** Subscribe to sync progress: cb({ pendingCount, syncing, lastResult? }). */
export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

async function notify(extra) {
  let pendingCount = 0;
  try { pendingCount = await count(); } catch { /* idb read best-effort */ }
  for (const cb of subscribers) {
    try { cb({ pendingCount, syncing, ...(extra || {}) }); } catch { /* a bad subscriber must not wedge sync */ }
  }
}

/**
 * Drain the outbox once. No-op when offline or already syncing.
 * @returns {Promise<{synced:number, failed:number, dropped:number, skipped?:string}>}
 */
export async function syncNow() {
  if (syncing) return { synced: 0, failed: 0, dropped: 0, skipped: 'busy' };
  if (!getOnline()) return { synced: 0, failed: 0, dropped: 0, skipped: 'offline' };

  syncing = true;
  await notify();
  let synced = 0;
  let failed = 0;
  let dropped = 0;
  // TICKET-654: named record of every op dropped this drain, so the UI can tell
  // the user WHAT was lost (a photo silently vanishing after 8 retries with only
  // a spine log row is the "silent fail" bug — spine coverage isn't user coverage).
  const droppedOps = [];
  // TICKET-815: which ops actually landed this drain, so a gallery can clear the
  // optimistic tile for THOSE captures instead of waiting for the whole outbox
  // to reach zero (one stuck op held every other tile on "Uploading...").
  const syncedIds = [];
  // TICKET-664: every op that is PERMANENTLY resolved this drain — synced OR
  // terminally dropped after MAX_RETRIES. A dropped op never appears in
  // syncedIds (it didn't land), so a consumer that only clears on syncedIds
  // leaves that capture's optimistic tile on "Uploading..." forever even
  // though the outbox will never retry it again. clearedIds is the superset
  // UI state should key off to remove a resolved tile (success or terminal
  // failure alike) — the existing droppedOps toast still tells the user it failed.
  const clearedIds = [];
  let drainThrew = false;

  try {
    // Drain in PASSES until the outbox is empty. A single listPending() snapshot
    // misses ops enqueued WHILE this drain runs — e.g. a multi-photo batch
    // enqueues op-by-op and each capture's syncNow() no-ops on the `syncing`
    // guard above. Without re-reading, those ops strand: pendingCount never
    // returns to 0, so the photos reconcile (shouldReconcileAfterSync) never
    // fires and the optimistic "Uploading…" tiles never clear (TICKET-071). Loop
    // while a pass makes forward progress (synced/dropped) and work remains; a
    // network/offline break, or a pass with no progress (only server-rejections),
    // stops the loop — those ops retry on the next online tick / capture.
    let netBreak = false;
    for (;;) {
      // TICKET-1105: ids only, then ONE op in memory at a time. A getAll() here
      // materialised every queued photo's bytes at once and crashed WebKit's
      // IDB/network process on iOS.
      const pendingIds = await listPendingIds();
      if (pendingIds.length === 0) break;
      const progressBefore = synced + dropped; // track only successful/dropped progress

      for (const localId of pendingIds) {
        const op = await getOp(localId);
        if (!op) continue; // removed meanwhile (another drain / clear)
        // Poison-op guard: a non-network failure retried too many times is dropped
        // so it can never wedge the queue forever.
        if ((op.retries || 0) >= MAX_RETRIES) {
          await remove(op.local_id);
          dropped++;
          droppedOps.push({ domain: op.domain, type: op.type, entity_id: op.entity_id, error: op.error });
          clearedIds.push(op.entity_id);
          logAppError({ type: 'offline_sync_dropped', message: `dropped ${op.domain}.${op.type} after ${op.retries} retries`, metadata: { entity_id: op.entity_id, error: op.error } });
          continue;
        }

        const fn = appliers[op.domain] && appliers[op.domain][op.type];
        if (!fn) {
          // TICKET-1035: mirror the MAX_RETRIES drop path above — an unknown op
          // type used to drop silently (count only), leaving the UI nothing to
          // surface and app_error_logs nothing to debug from.
          await remove(op.local_id);
          dropped++;
          droppedOps.push({ domain: op.domain, type: op.type, entity_id: op.entity_id, error: 'unknown_op_type' });
          clearedIds.push(op.entity_id);
          logAppError({ type: 'offline_sync_dropped', message: `dropped ${op.domain}.${op.type}: unknown op type`, metadata: { entity_id: op.entity_id } });
          continue;
        }

        try {
          const res = await fn(op);
          if (res && res.success === false) {
            if (op.type.startsWith('create') && DUP_RE.test(res.error || '')) {
              await remove(op.local_id); // already exists server-side → synced
              synced++;
              syncedIds.push(op.entity_id);
              clearedIds.push(op.entity_id);
              continue;
            }
            if (isNetworkErr(res.error) || !getOnline()) {
              await markFailed(op.local_id, res.error);
              failed++;
              netBreak = true;
              break; // stop; preserve order; retry the whole queue next online tick
            }
            await markFailed(op.local_id, res.error); // server rejection → retry until MAX
            failed++;
            // Emit to the spine — a silent markFailed here is exactly why a
            // stuck offline drain (e.g. a photo that won't register) left no
            // trace in app_error_logs to debug from.
            logAppError({ type: 'offline_sync_op_failed', message: `${op.domain}.${op.type} rejected: ${res.error || 'unknown'}`, metadata: { entity_id: op.entity_id, retries: (op.retries || 0) + 1 } });
            continue;
          }
          await remove(op.local_id);
          synced++;
          syncedIds.push(op.entity_id);
          clearedIds.push(op.entity_id);
        } catch (e) {
          await markFailed(op.local_id, e.message);
          failed++;
          logAppError({ type: 'offline_sync_op_error', message: `${op.domain}.${op.type} threw: ${e.message}`, stack: e.stack, metadata: { entity_id: op.entity_id, retries: (op.retries || 0) + 1 } });
          if (!getOnline()) { netBreak = true; break; }
        }
      }

      if (netBreak) break;       // network/offline — resume on the next online tick
      if (!getOnline()) break;
      // No forward progress this pass (synced+dropped didn't advance) → stop so
      // we don't hot-loop. A pass where only server-rejections occurred has no
      // synced/dropped gain, so the loop exits and those ops retry on the next
      // online tick rather than being hammered again immediately.
      if ((synced + dropped) === progressBefore) break;
    }
  } catch (e) {
    // TICKET-664: e.g. IndexedDB force-closing the connection mid-drain (observed
    // 2026-09-22: "Failed to execute 'transaction' on 'IDBDatabase': The database
    // connection is closing" — WebKit can do this on backgrounding). No op gets
    // processed, so `failed` stays 0 and the retry below used to no-op — the whole
    // outbox then sits parked until an unrelated online/capture event happens to
    // fire again. Treat a drain that threw before touching any op the same as a
    // failed one so the bounded backoff still re-attempts it.
    drainThrew = true;
    logAppError({ type: 'offline_sync_error', message: e.message, stack: e.stack });
  } finally {
    syncing = false;
    await notify({ lastResult: { synced, failed, dropped, droppedOps, syncedIds, clearedIds } });
    scheduleRetry(synced + dropped > 0, failed || drainThrew);
  }
  return { synced, failed, dropped, droppedOps, syncedIds, clearedIds };
}

/**
 * TICKET-815 — re-drain after a pass that left work behind. Bounded: three
 * attempts on a growing backoff, and any forward progress resets the counter so
 * a long batch never starves. No timer while offline (the online tick covers it).
 */
function scheduleRetry(madeProgress, failed) {
  if (madeProgress) retryAttempt = 0;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (!failed || !getOnline()) return;
  if (retryAttempt >= retryBackoff.length) return;
  const delay = retryBackoff[retryAttempt];
  retryAttempt++;
  retryTimer = setTimeout(() => { retryTimer = null; syncNow(); }, delay);
}

/** Start the engine: drain now (if online) and on every reconnect. */
export function start() {
  if (running) return;
  running = true;
  netUnsub = subscribeNet((online) => { if (online) { syncNow(); } });
  if (getOnline()) { syncNow(); }
}

/** Stop the engine. */
export function stop() {
  running = false;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (netUnsub) { netUnsub(); netUnsub = null; }
}

export function isRunning() { return running; }
